import React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Box, Flex, Link, Text } from '@workwhile/ui'
import { UserPaymentProvider, UserPaymentEventType } from 'api/payments'
import {
  UserPayment,
  UserPaymentItem,
  UserPaymentEventSubType,
  HighnoteUserPayment,
} from 'api/payments/getUserPayments'
import { toTitleCase } from 'lib/string'
import { AmountRender } from 'pages/UserPayments/components/UserPaymentsRenderers/AmountRender'
import { EvereeProviderCell } from 'pages/UserPayments/components/UserPaymentsRenderers/EvereeProviderCell'
import { HighnoteProviderCell } from 'pages/UserPayments/components/UserPaymentsRenderers/HighnoteProviderCell'
import {
  HighnotePaymentStatusRender,
  PaymentStatusRender,
} from 'pages/UserPayments/components/UserPaymentsRenderers/PaymentStatusRender'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { PaymentRowActions } from '../PaymentRowActions'
import { TimeConversionPopover } from 'components/Shared/TimeConversion'
import { getLocalTimezone } from 'lib/time'

const EventTypeCell = ({
  eventType,
  subType,
  shiftId,
  positionId,
  shiftPositionName,
  companyId,
  companyName,
}: {
  eventType: UserPaymentEventType
  subType: UserPaymentEventSubType | null
  shiftId: number | null
  positionId: number | null
  shiftPositionName: string | null
  companyId: number | null
  companyName: string | null
}) => {
  const eventTypeDisplay = toTitleCase(eventType)
  const hasShiftInfo = shiftId || positionId || companyId

  if (hasShiftInfo) {
    return (
      <Flex flexDirection="column" gap={0.5}>
        <Text>
          <Text fontWeight="bold">{eventTypeDisplay}</Text>
        </Text>
        {subType ? (
          <Text color="lightText">Sub type: {toTitleCase(subType)}</Text>
        ) : null}
        {shiftId ? (
          <Text fontSize="sm">
            Shift ID: (
            <CopyableLink label={shiftId} link={`/shifts/${shiftId}`} />)
          </Text>
        ) : null}
        {positionId && shiftPositionName ? (
          <Text fontSize="sm">
            Position: {shiftPositionName} (
            <CopyableLink
              label={positionId}
              link={`/position_editor?positionId=${positionId}`}
            />
            )
          </Text>
        ) : null}
        {companyId && companyName ? (
          <Text fontSize="sm">
            Company: {companyName} (
            <CopyableLink label={companyId} link={`/companies/${companyId}`} />)
          </Text>
        ) : null}
      </Flex>
    )
  }

  // Capitalize first letter of event type
  return (
    <Text>
      <Text fontWeight="bold">{eventTypeDisplay}</Text>
      {subType ? (
        <Text color="lightText">Subtype: {toTitleCase(subType)}</Text>
      ) : null}
    </Text>
  )
}

export const ProviderCell = ({ payment }: { payment: UserPayment }) => {
  switch (payment.paymentProvider) {
    case UserPaymentProvider.STRIPE: {
      const { stripeId, stripeAccountIdProd, eventType } = payment
      // Use transfer link for shift and bonus, payout link for paycheck and instant_payout
      const isPayout =
        eventType === UserPaymentEventType.INSTANT_PAYOUT ||
        eventType === UserPaymentEventType.PAYCHECK
      const linkUrl = stripeId
        ? isPayout
          ? `https://dashboard.stripe.com/${stripeAccountIdProd}/connect/view-as/${stripeAccountIdProd}/payouts/${stripeId}`
          : `https://dashboard.stripe.com/connect/transfers/${stripeId}`
        : ''

      return (
        <Flex flexDirection="column" gap={0.5}>
          <Text>
            <Text fontWeight="bold">Stripe</Text>
          </Text>
          {stripeAccountIdProd ? (
            <Text fontSize="sm">
              Account:{' '}
              <Link
                to={`https://dashboard.stripe.com/connect/accounts/${stripeAccountIdProd}`}
                target="_blank"
              >
                {stripeAccountIdProd}
              </Link>
            </Text>
          ) : null}

          {stripeId ? (
            <Text fontSize="sm">
              {isPayout ? 'Payout' : 'Transfer'}
              {': '}
              <Link to={linkUrl} target="_blank">
                {stripeId}
              </Link>
            </Text>
          ) : null}
        </Flex>
      )
    }

    case UserPaymentProvider.MARQETA: {
      const { marqetaEnvironment, marqetaAccountId, marqetaGpaToken } = payment
      if (!marqetaEnvironment) {
        return (
          <Text>
            <Text fontWeight="bold">Marqeta</Text> (N/A)
          </Text>
        )
      }
      if (marqetaEnvironment === 'sandbox') {
        return (
          <Text>
            <Text fontWeight="bold">Marqeta</Text> (Sandbox)
          </Text>
        )
      }
      return (
        <Flex flexDirection="column" gap={0.5}>
          <Text>
            <Text fontWeight="bold">Marqeta</Text>
          </Text>
          {marqetaAccountId ? (
            <Text fontSize="sm">
              Account:{' '}
              <Link
                to={`https://app.marqeta.com/program/user?program=wclgprew&tab=Transactions&token=${marqetaAccountId}`}
                target="_blank"
              >
                {marqetaAccountId}
              </Link>
            </Text>
          ) : null}
          {marqetaGpaToken ? (
            <Text fontSize="sm">GPA Token: {marqetaGpaToken}</Text>
          ) : null}
        </Flex>
      )
    }
    case UserPaymentProvider.EVEREE:
      return <EvereeProviderCell payment={payment} />
    case UserPaymentProvider.BBSI:
      return (
        <Text>
          <Text fontWeight="bold">BBSI</Text> (Legacy W2 provider, not in use)
        </Text>
      )
    case UserPaymentProvider.HIGHNOTE:
      return <HighnoteProviderCell payment={payment} />
    default:
      return <Text>-</Text>
  }
}

/**
 *
 *
 *  Non-selectable columns go here. They're exported out to be used directly in the table.
 *
 *
 */

export const ActionsColumn: ColumnDef<UserPayment> = {
  id: 'actions',
  header: 'Actions',
  accessorKey: 'actions',
  size: 70,
  minSize: 70,
  cell: ({ row }) => <PaymentRowActions payment={row.original} />,
}

export const IDColumn: ColumnDef<UserPayment> = {
  header: 'ID',
  accessorKey: 'id',
  minSize: 50,
  cell: ({ row }) => (
    <CopyableLink
      label={row.original.id}
      link={`/user_payments?payment-id=${row.original.id}`}
    />
  ),
}

export const UserColumn: ColumnDef<UserPayment> = {
  header: 'User',
  accessorKey: 'userId',
  minSize: 150,
  cell: ({ row }) => {
    const userId = row.original.userId
    const userName = row.original.userName
    const displayName = userName || ''
    return (
      <Text>
        {displayName ? (
          <>
            {displayName}{' '}
            <Text as="span" style={{ whiteSpace: 'nowrap' }}>
              (<CopyableLink label={userId} link={`/users/${userId}`} />)
            </Text>
          </>
        ) : (
          <CopyableLink label={userId} link={`/users/${userId}`} />
        )}
      </Text>
    )
  },
}

export const AmountColumn: ColumnDef<UserPayment> = {
  header: 'Amount',
  accessorKey: 'amount',
  minSize: 120,
  cell: ({ row }) => <AmountRender value={row.original.amount} />,
}

export const StatusColumn: ColumnDef<UserPayment> = {
  header: 'Status',
  accessorKey: 'status',
  minSize: 120,
  cell: ({ row }) => {
    const payment = row.original

    if (payment.paymentProvider === UserPaymentProvider.HIGHNOTE) {
      return (
        <HighnotePaymentStatusRender payment={payment as HighnoteUserPayment} />
      )
    }

    return <PaymentStatusRender value={payment.status} />
  },
}

export const TypeColumn: ColumnDef<UserPayment> = {
  header: 'Type',
  accessorKey: 'w2',
  minSize: 80,
  cell: ({ row }) => <Text>{row.original.w2 ? 'W2' : '1099'}</Text>,
}

export const EventTypeColumn: ColumnDef<UserPayment> = {
  header: 'Event Type',
  accessorKey: 'eventType',
  minSize: 180,
  cell: ({ row }) => (
    <EventTypeCell
      eventType={row.original.eventType}
      subType={row.original.eventSubType}
      shiftId={row.original.shiftId}
      positionId={row.original.positionId}
      shiftPositionName={row.original.positionName}
      companyId={row.original.companyId}
      companyName={row.original.companyName}
    />
  ),
}

// Selectable columns
// Full Provider Column with all details
export const ProviderColumn: ColumnDef<UserPayment> = {
  header: 'Provider',
  accessorKey: 'paymentProvider',
  minSize: 200,
  cell: ({ row }) => <ProviderCell payment={row.original} />,
}

const OriginatedAtColumn: ColumnDef<UserPayment> = {
  header: 'Originated At',
  accessorKey: 'originatedAt',
  minSize: 180,
  cell: ({ row }) => {
    const targetTimezone = row.original.userTimezone
    return (
      <TimeConversionPopover
        value={row.original.originatedAt}
        zones={[
          { timeZone: getLocalTimezone(), context: 'Your computer' },
          { timeZone: targetTimezone, context: 'Worker' },
          { timeZone: 'UTC' },
        ]}
        primaryTimeZone={targetTimezone}
      />
    )
  },
}

const CreatedAtColumn: ColumnDef<UserPayment> = {
  header: 'Created At',
  accessorKey: 'createdAt',
  minSize: 180,
  cell: ({ row }) => {
    const targetTimezone = row.original.userTimezone
    return (
      <TimeConversionPopover
        value={row.original.createdAt}
        zones={[
          { timeZone: getLocalTimezone(), context: 'Your computer' },
          { timeZone: targetTimezone, context: 'Worker' },
          { timeZone: 'UTC' },
        ]}
        primaryTimeZone={targetTimezone}
      />
    )
  },
}

export const CommentColumn: ColumnDef<UserPayment> = {
  header: 'Comment',
  accessorKey: 'comment',
  minSize: 150,
  cell: ({ row }) => (
    <Text style={{ whiteSpace: 'pre-line' }}>
      {row.original.comment || '-'}
    </Text>
  ),
}

export const DescriptionColumn: ColumnDef<UserPayment> = {
  header: 'Description',
  accessorKey: 'description',
  minSize: 200,
  cell: ({ row }) => {
    const eventTypeDisplay = toTitleCase(row.original.eventType)
    const comment = row.original.comment

    // If comment exists and is not empty or just a dash, combine them
    if (comment && comment.trim() && comment.trim() !== '-') {
      return (
        <Text>
          <Text fontWeight="bold">{eventTypeDisplay}:</Text>
          <Text>{comment}</Text>
        </Text>
      )
    }

    // Otherwise, just show the event type
    return (
      <Text>
        <Text fontWeight="bold">{eventTypeDisplay}</Text>
      </Text>
    )
  },
}

export const ItemsColumn: ColumnDef<UserPayment> = {
  header: 'Items',
  accessorKey: 'items',
  minSize: 200,
  cell: ({ row }) => {
    const items = row.original.items || []

    if (items.length === 0) {
      return <Text>-</Text>
    }

    return (
      <Box
        as="ul"
        m={0}
        p={0}
        style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0 }}
      >
        {items.map((item: UserPaymentItem) => (
          <Box
            as="li"
            key={item.id}
            mb={0.5}
            style={{ marginLeft: 0, paddingLeft: 0 }}
          >
            <Text fontSize="sm" style={{ lineHeight: '1.5' }}>
              {toTitleCase(item.itemType)}: <AmountRender value={item.amount} />
              {item.payingEntity && item.payingEntity !== 'not_applicable' ? (
                <Text color="lightText" as="span">
                  {' '}
                  (paid by {toTitleCase(item.payingEntity)})
                </Text>
              ) : null}
            </Text>
          </Box>
        ))}
      </Box>
    )
  },
}

export const PaymentIDColumn: ColumnDef<UserPayment> = {
  header: 'Payment ID',
  accessorKey: 'id',
  minSize: 50,
  cell: ({ row }) => (
    <CopyableLink
      label={row.original.id}
      link={`/user_payments?payment-id=${row.original.id}`}
    />
  ),
}

export const selectableColumns = [
  {
    id: 'user',
    label: 'User',
    columnDef: UserColumn,
  },
  {
    id: 'provider',
    label: 'Provider',
    columnDef: ProviderColumn,
  },
  {
    id: 'originated-at',
    label: 'Originated At',
    columnDef: OriginatedAtColumn,
  },
  {
    id: 'created-at',
    label: 'Created At',
    columnDef: CreatedAtColumn,
  },
  {
    id: 'comment',
    label: 'Comment',
    columnDef: CommentColumn,
  },
  {
    id: 'description',
    label: 'Description',
    columnDef: DescriptionColumn,
  },
  {
    id: 'items',
    label: 'Items',
    columnDef: ItemsColumn,
  },
] as const

export type SelectableColumns = (typeof selectableColumns)[number]['id']
